/**
 * @file 金额输入区
 */
import React from 'react';
import { usePoolInfoContext } from '@/hooks/usePoolInfo';
import { StakeType, GasStore } from '@/types/stake';
import cls from 'classnames';
import { useBalance, useInputStatus, useToSwap, useDeposite } from './hooks';
import { CoinInfo } from '@/types/common';
import BigNumber from 'bignumber.js';
import { useWallet } from '@/hooks/wallet';
import { Web3AssetsIcon } from '@bfe/moly-icons';
import { formatUSD } from '@/utils/num';
import { Web3DepositModal } from '@web3fe/infra';
import PlusIcon from '@/assets/images/bybitsol/plus.svg';
import ExchangeIcon from '@/assets/images/bybitsol/exchange.svg';
import { formatNumber } from '@web3/base-utils';

import styles from './index.module.scss';

interface Props {
  stakeAmount: string;
  gasStore: GasStore;
  currentStakeToken: CoinInfo;
  onChangeTextError: (v: string) => void;
  onChangeAmount: (v: string) => void;
  onChangeCurrentToken: (key: string) => void;
}

export const InputArea = (props: Props) => {
  const {
    stakeAmount,
    gasStore,
    currentStakeToken,
    onChangeAmount,
    onChangeTextError,
  } = props;

  const { poolInfo } = usePoolInfoContext();
  const { balance, balanceIsLoading, nativeBalance } = useBalance({
    currentStakeToken,
  });
  const { isShowMax } = useInputStatus({
    stakeAmount,
    balance,
    nativeBalance,
    gasStore,
    currentStakeToken,
    onChangeStakeAmount: onChangeAmount,
    onChangeTextError,
  });

  const { toSwap } = useToSwap(currentStakeToken);
  const { isLogin, chainMap } = useWallet();
  const { showDeposit, onChangeShowDeposite } = useDeposite();

  return (
    <>
      <div className={cls(styles.container)}>
        {/* 右上角余额 */}
        <div className={styles.currentTokenRight}>
          <Web3AssetsIcon className={styles.walletIcon} />
          <div className={styles.balance}>{`${
            balance
              ? formatNumber(+balance ? +balance : 0, {
                  kmb: true,
                  decimal: 4,
                  smallNumberUseValidDigits: false,
                  collapseZero: false,
                })
              : '--'
          } ${currentStakeToken.symbol}`}</div>
          {/* 暂时屏蔽stx，因为还不支持 */}
          {!isShowMax && !balanceIsLoading && poolInfo.chainCode !== 'STX' && (
            <PlusIcon
              className={styles.deposite}
              onClick={() => {
                onChangeShowDeposite(true);
              }}
            />
          )}
          {/* 只有登陆并且池子类型为再质押时，提供去swap功能 */}
          {isLogin && poolInfo.stakeType === StakeType.Restaking && (
            <ExchangeIcon onClick={toSwap} className={styles.toSwap} />
          )}
        </div>
        {/* 输入框 */}
        <div className={styles.inputArea}>
          <div className={styles.inputToken}>
            <img
              src={chainMap?.[poolInfo.chainCode]?.iconNightUrl}
              className={styles.inputTokenIcon}
            />
            <div className={styles.inputTokenSymbol}>
              {currentStakeToken?.symbol}
            </div>
          </div>
          <div>
            <input
              type="number"
              className={styles.input}
              value={stakeAmount}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                onChangeAmount(event.target.value);
              }}
              placeholder="0.00"
            />
            <div className={styles.stakeAmountValue}>
              {currentStakeToken?.price && stakeAmount
                ? formatUSD(
                    BigNumber(stakeAmount)
                      .times(currentStakeToken?.price)
                      .toString(),
                    4
                  )
                : '= $0.00'}
            </div>
          </div>
        </div>
      </div>
      <Web3DepositModal
        visible={showDeposit}
        token={{
          chainCode: poolInfo.chainCode,
          token: currentStakeToken.symbol,
        }}
        onClose={() => {
          onChangeShowDeposite(false);
        }}
      />
    </>
  );
};
